/**
 * AlertRowCompact - Single alert row within a HostIncidentCard
 *
 * Compact density, shows severity badge, alert name, ack/mute state,
 * and action buttons.
 */

import { useTranslation } from 'react-i18next'
import {
  Badge, Button, Text, SimpleTooltip,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@agion/ui'
import {
  CheckCircle2, BellOff, Clock, Check, Bell, Timer,
} from 'lucide-react'
import { formatRelativeTime, cn } from '@/core/infra_utils'
import type { ActiveAlert, MuteDuration } from '@/core/infra_types'
import { SeverityBadge } from './MonitoringPrimitives'
import { deriveAlertName, formatCountdown } from './alert_helpers'

interface AlertRowCompactProps {
  alert: ActiveAlert
  isSelected: boolean
  onSelect: () => void
  onAck: () => void
  onUnack: () => void
  onMute: (duration: MuteDuration) => void
  onUnmute: () => void
  isMutating: boolean
}

export function AlertRowCompact({
  alert,
  isSelected,
  onSelect,
  onAck,
  onUnack,
  onMute,
  onUnmute,
  isMutating,
}: AlertRowCompactProps) {
  const { t } = useTranslation(['pages'])
  const alertName = deriveAlertName(alert)
  const severityLabel = t(`pages:monitoring.alerts.severity.${alert.severity}`, alert.severity)

  const isAcked = Boolean(alert.ackedBy)
  const isMuted = alert.mutedUntil && new Date(alert.mutedUntil) > new Date()
  const isResolved = alert.status === 'resolved'

  const mutedCountdown = isMuted ? formatCountdown(new Date(alert.mutedUntil!)) : null

  const SELECTION_RING = 'ring-2 ring-amber-500/70 ring-offset-1 ring-offset-white dark:ring-offset-slate-900'

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={e => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onSelect()
        }
      }}
      className={cn(
        'w-full flex items-center gap-2 px-2 py-1.5 rounded text-left cursor-pointer',
        'hover:bg-slate-50 dark:hover:bg-slate-800/50',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
        isSelected && SELECTION_RING,
        isResolved && 'opacity-60'
      )}
      aria-pressed={isSelected}
      aria-label={t('pages:monitoring.alerts.selectAlert', 'Select alert: {{name}}', { name: alertName })}
    >
      <SeverityBadge
        severity={alert.severity}
        label={severityLabel}
        ariaLabel={t('pages:monitoring.alerts.severityAriaLabel', '{{severity}} severity', { severity: severityLabel })}
        className="flex-shrink-0"
      />

      <div className="min-w-0 flex-1">
        <Text size="sm" className="truncate font-medium text-slate-900 dark:text-slate-100">
          {alertName}
        </Text>
        <div className="flex items-center gap-2 mt-0.5">
          <Text size="xs" color="muted" className="flex items-center gap-1">
            <Clock size={10} />
            {formatRelativeTime(alert.startsAt)}
          </Text>
          {isAcked && (
            <Text size="xs" color="muted" className="flex items-center gap-1">
              <CheckCircle2 size={10} className="text-emerald-500" />
              {alert.ackedBy}
            </Text>
          )}
          {isMuted && mutedCountdown && (
            <Text size="xs" color="muted" className="flex items-center gap-1">
              <BellOff size={10} className="text-amber-500" />
              {mutedCountdown}
            </Text>
          )}
          {isResolved && (
            <Badge variant="default" className="text-xs">
              {t('pages:monitoring.alertDetail.status.resolved', 'Resolved')}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {!isResolved && (
          <>
            <SimpleTooltip content={isAcked ? t('pages:monitoring.alerts.unack', 'Remove acknowledgment') : t('pages:monitoring.alerts.ack', 'Acknowledge')}>
              <Button
                variant="ghost"
                size="sm"
                onClick={e => {
                  e.stopPropagation()
                  isAcked ? onUnack() : onAck()
                }}
                disabled={isMutating}
                className={cn('h-7 w-7 p-0', isAcked && 'text-emerald-600')}
                aria-label={isAcked ? t('pages:monitoring.alerts.unack', 'Remove acknowledgment') : t('pages:monitoring.alerts.ack', 'Acknowledge')}
              >
                <Check size={14} />
              </Button>
            </SimpleTooltip>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => e.stopPropagation()}
                  disabled={isMutating}
                  className={cn('h-7 w-7 p-0', isMuted && 'text-amber-600')}
                  aria-label={t('pages:monitoring.alerts.muteOptions', 'Mute options')}
                >
                  {isMuted ? <BellOff size={14} /> : <Bell size={14} />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isMuted ? (
                  <DropdownMenuItem onClick={() => onUnmute()}>
                    <Bell size={14} className="mr-2" />
                    {t('pages:monitoring.alerts.unmute', 'Unmute')}
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => onMute('1h')}>
                      <Timer size={14} className="mr-2" />
                      {t('pages:monitoring.alerts.mute1h', 'Mute for 1 hour')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onMute('4h')}>
                      <Timer size={14} className="mr-2" />
                      {t('pages:monitoring.alerts.mute4h', 'Mute for 4 hours')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onMute('24h')}>
                      <Timer size={14} className="mr-2" />
                      {t('pages:monitoring.alerts.mute24h', 'Mute for 24 hours')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    </div>
  )
}

